import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy, where } from "firebase/firestore";
import React from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import * as api from "../../api/api";
import { useOrganizationCollection } from "../../api/firestoreWrappers.generated.jsx";
import CancelButton from "../../components/CancelButton";
import BooleanFormGroup from "../../components/FormGroups/BooleanFormGroup.jsx";
import SelectFormGroup from "../../components/FormGroups/SelectFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import UserMakeAdminModal from "../../components/UserMakeAdminModal/index.jsx";
import urls from "../../urls.jsx";

export default function UserSettingsPage() {
    const { uid } = useParams();
    const [user, setUser] = React.useState(null);
    const [error, setError] = React.useState("");
    const [showUserMakeAdminModal, setShowUserMakeAdminModal] =
        React.useState(false);
    const [pendingUserUpdate, setPendingUserUpdate] = React.useState(null);

    const [organizations] = useOrganizationCollection([
        where("deleted", "==", false),
        orderBy("name", "asc"),
    ]);
    const navigate = useNavigate();

    const organizationOptions = organizations.map((organization) => ({
        id: organization?.id,
        name: organization?.name,
    }));

    const userSettingsConfigSchema = z.object({
        organizationId: z
            .string()
            .min(1, { message: "Organization is required" }),
        isAdmin: z.boolean(),
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(userSettingsConfigSchema),
        defaultValues: {
            organizationId: "",
            isAdmin: false,
        },
    });

    const organizationId = watch("organizationId");

    React.useEffect(() => {
        let active = true;

        const fetchUser = async () => {
            try {
                const user = await api.getSingleUserDetails(uid);
                if (active) setUser(user);
            } catch (error) {
                setError(error.message ?? String(error));
            }
        };

        fetchUser();
        return () => {
            active = false;
        };
    }, [uid]);

    React.useEffect(() => {
        if (!user) return;

        reset({
            organizationId: user.customClaims?.organizationId ?? "",
            isAdmin: user.customClaims?.isAdmin ?? false,
        });
    }, [user, reset]);

    const onSubmit = async (userSettingsUpdate) => {
        const makingAdmin =
            userSettingsUpdate.isAdmin === true &&
            user.customClaims?.isAdmin !== true &&
            userSettingsUpdate.organizationId === api.CORVUS_ORGANIZATION_ID;

        if (makingAdmin) {
            setPendingUserUpdate(userSettingsUpdate);
            setShowUserMakeAdminModal(true);
            return;
        }

        await submitUserUpdate(userSettingsUpdate);
    };

    const submitUserUpdate = async (userSettingsUpdate) => {
        const payload = { ...userSettingsUpdate };

        if (payload.organizationId !== api.CORVUS_ORGANIZATION_ID) {
            payload.isAdmin = false;
        }

        try {
            await api.updateUser({
                uid,
                ...payload,
            });
            navigate(urls.users());
        } catch (error) {
            setError(error.message ?? String(error));
        }
    };

    return (
        <Layout>
            <title>{"User Settings | MyCorvus"}</title>
            <UserMakeAdminModal
                userEmail={user?.email}
                show={showUserMakeAdminModal}
                onCancel={() => {
                    setPendingUserUpdate(null);
                    setShowUserMakeAdminModal(false);
                }}
                onProceed={async () => {
                    await submitUserUpdate(pendingUserUpdate);
                    setPendingUserUpdate(null);
                    setShowUserMakeAdminModal(false);
                }}
            />
            <Card className="p-0">
                <Title title={"User Settings"} backButtonUrl={urls.users()}>
                    {user?.email}
                </Title>
                <Card.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            onClose={() => setError("")}
                            dismissible
                        >
                            {error}
                        </Alert>
                    )}
                    {user === null ? (
                        <div className="d-flex justify-content-center align-items-center p-2">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </Spinner>
                        </div>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <SelectFormGroup
                                label="Organization"
                                name="organizationId"
                                control={control}
                                errors={errors}
                                setValue={setValue}
                                disabledOption={{
                                    id: "none",
                                    name: "Choose an organization",
                                }}
                                options={organizationOptions}
                            />
                            {organizationId === api.CORVUS_ORGANIZATION_ID && (
                                <BooleanFormGroup
                                    label="User is admin"
                                    name="isAdmin"
                                    control={control}
                                    errors={errors}
                                    setValue={setValue}
                                />
                            )}
                            <Card.Body className="p-0">
                                <Button
                                    variant="success"
                                    disabled={isSubmitting || !isDirty}
                                    type="submit"
                                    className="me-2"
                                >
                                    <FaSave className="mb-1 me-1" />
                                    {isSubmitting ? "Saving..." : "Save"}
                                </Button>
                                <CancelButton to={urls.users()} />
                            </Card.Body>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
